import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import FeedbackButton from "@/components/app/FeedbackButton";
import VersionWatcher from "@/components/app/VersionWatcher";
import InstallPrompt from "@/components/app/InstallPrompt";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import WorldCup from "@/pages/WorldCup";
import Onboarding from "@/pages/Onboarding";
import Welcome from "@/pages/Welcome";
import Discover from "@/pages/Discover";
import Matches from "@/pages/Matches";
import Chat from "@/pages/Chat";
import Profile from "@/pages/Profile";
import Fixtures from "@/pages/Fixtures";
import Banter from "@/pages/Banter";
import ShareQR from "@/pages/ShareQR";
import Grounds from "@/pages/Grounds";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Guidelines from "@/pages/Guidelines";
import AdminReports from "@/pages/AdminReports";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminAnalytics from "@/pages/AdminAnalytics";

function LandingOrRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) {
    // If a pub-invite is stashed (from /checkin?invite_from=…), jump straight to /fixtures
    // instead of the default /discover — the effect there will run the auto-say-hi flow.
    try {
      const stashed = JSON.parse(localStorage.getItem("fm_invite") || "null");
      const fresh = stashed && (Date.now() - (stashed.ts || 0) < 24 * 60 * 60 * 1000);
      if (fresh && user.profile_complete) return <Navigate to="/fixtures" replace />;
    } catch { /* ignore */ }
    return <Navigate to={user.profile_complete ? "/discover" : "/onboarding"} replace />;
  }
  return <Landing />;
}

// Preserves the query string when redirecting /checkin?fixture_id=…&source=…&venue=…&invite_from=…
// to /fixtures so the deep-linked QR flow can auto-open the check-in modal (or run the
// one-tap "meet at the ground" auto-say-hi flow when invite_from is present).
// Also stashes the invite in localStorage so a guest who has to log in / register first
// can still land in the right place after auth.
function CheckinRedirect() {
  const location = useLocation();
  try {
    const params = new URLSearchParams(location.search);
    if (params.get("fixture_id") && params.get("source")) {
      localStorage.setItem("fm_invite", JSON.stringify({
        fixture_id: params.get("fixture_id"),
        source: params.get("source"),
        venue: params.get("venue") || "",
        invite_from: params.get("invite_from") || "",
        ts: Date.now(),
      }));
    }
  } catch { /* ignore quota */ }
  return <Navigate to={`/fixtures${location.search}`} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingOrRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/guidelines" element={<Guidelines />} />
            <Route path="/onboarding" element={<ProtectedRoute requireProfile={false}><Onboarding /></ProtectedRoute>} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
            <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
            <Route path="/fixtures" element={<ProtectedRoute><Fixtures /></ProtectedRoute>} />
            <Route path="/banter" element={<ProtectedRoute><Banter /></ProtectedRoute>} />
            <Route path="/share" element={<ProtectedRoute><ShareQR /></ProtectedRoute>} />
            <Route path="/grounds" element={<ProtectedRoute><Grounds /></ProtectedRoute>} />
            <Route path="/grounds/:userId" element={<ProtectedRoute requireProfile={false}><Grounds /></ProtectedRoute>} />
            <Route path="/checkin" element={<CheckinRedirect />} />
            <Route path="/worldcup" element={<ProtectedRoute><WorldCup /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
            <Route path="/chat/:matchId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute requireProfile={false}><AdminReports /></ProtectedRoute>} />
            <Route path="/admin/feedback" element={<ProtectedRoute requireProfile={false}><AdminFeedback /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute requireProfile={false}><AdminAnalytics /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <FeedbackButton />
          <VersionWatcher />
          <InstallPrompt />
        </BrowserRouter>
        <Toaster position="top-center" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
